import React, { useEffect, useState, useCallback, useMemo } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { fmtDate } from "@/lib/constants";
import { QUESTION_CATEGORIES, QUESTION_STATUSES, QUESTION_STATUS_STYLES, PRIORITIES, PRIORITY_STYLES } from "@/lib/reconConstants";
import { MessagesSquare, Plus, AlertTriangle, Clock, ArrowRight, Trash2, Link2 } from "lucide-react";
import { RecordActionsMenu } from "@/components/RecordActions";

const CAT_LABEL = Object.fromEntries(QUESTION_CATEGORIES.map((c) => [c.value, c.label]));
const isOverdue = (q) => q.due_date && new Date(q.due_date) < new Date(new Date().toDateString()) && !["Resolved", "Closed"].includes(q.status);
const isCriticalOpen = (q) => ["Critical", "High"].includes(q.priority) && !["Resolved", "Closed"].includes(q.status);

const Badge = ({ className, children }) => (
  <span className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium border ${className}`}>{children}</span>
);

export default function ReconQuestions() {
  const [me, setMe] = useState(null);
  const [recons, setRecons] = useState([]);
  const [items, setItems] = useState([]);
  const [tab, setTab] = useState("complyx");
  const [reconFilter, setReconFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [showCreate, setShowCreate] = useState(false);
  const [selected, setSelected] = useState(null);
  const caps = me?.caps || {};

  const load = useCallback(() => {
    const params = { category: tab };
    if (reconFilter !== "all") params.reconciliation_id = reconFilter;
    if (statusFilter !== "all") params.status = statusFilter;
    api.get("/recon/questions", { params }).then((r) => setItems(r.data)).catch(() => {});
  }, [tab, reconFilter, statusFilter]);

  useEffect(() => { api.get("/recon/me").then((r) => setMe(r.data)).catch(() => {}); }, []);
  useEffect(() => { api.get("/recon/reconciliations").then((r) => setRecons(r.data)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);

  const counts = useMemo(() => ({
    overdue: items.filter(isOverdue).length,
    critical: items.filter(isCriticalOpen).length,
  }), [items]);

  return (
    <div>
      <header className="bg-navy px-6 md:px-8 py-6">
        <p className="overline text-gold mb-1.5">Operations · Admin Only</p>
        <h1 className="font-display text-2xl md:text-3xl font-bold text-white tracking-tight flex items-center gap-2">
          <MessagesSquare className="h-6 w-6 text-gold" aria-hidden="true" /> Questions Workspace
        </h1>
        <p className="text-slate-300 text-sm mt-2 max-w-3xl">Track clarifying questions to ComplyX, the Implementation Partner, and the Client. External delivery is tracked manually in this phase — mark a question "Sent" once you have delivered it.</p>
      </header>

      <div className="p-6 md:p-8 space-y-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <Tabs value={tab} onValueChange={(v) => { setTab(v); setSelected(null); }}>
            <TabsList data-testid="questions-tabs">
              {QUESTION_CATEGORIES.map((c) => (
                <TabsTrigger key={c.value} value={c.value} data-testid={`questions-tab-${c.value}`}>{c.label}</TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
          <Button className="bg-gold text-navy hover:bg-gold-dark" onClick={() => setShowCreate(true)} data-testid="questions-new-btn">
            <Plus className="h-4 w-4 mr-1.5" /> New Question
          </Button>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <div className="space-y-1.5"><Label className="text-xs">Reconciliation</Label>
            <Select value={reconFilter} onValueChange={setReconFilter}>
              <SelectTrigger className="w-[260px]" data-testid="questions-filter-recon"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All reconciliations</SelectItem>
                {recons.map((r) => <SelectItem key={r.id} value={r.id}>{r.customer_name} — {r.title || r.framework_name || r.status}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5"><Label className="text-xs">Status</Label>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[180px]" data-testid="questions-filter-status"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">All statuses</SelectItem>{QUESTION_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {counts.overdue > 0 && <Badge className="bg-red-100 text-red-800 border-red-200"><Clock className="h-3 w-3 mr-1" /> {counts.overdue} overdue</Badge>}
          {counts.critical > 0 && <Badge className="bg-amber-100 text-amber-800 border-amber-200"><AlertTriangle className="h-3 w-3 mr-1" /> {counts.critical} unresolved Critical/High</Badge>}
        </div>

        <div className="rounded-md border border-border bg-card overflow-hidden">
          <div className="overflow-x-auto"><table className="w-full text-sm">
            <thead><tr className="bg-slate-50 border-b border-border text-left">
              {["Question", "Customer", "Control / Source", "Priority", "Assigned", "Due", "Status", ""].map((h) => <th key={h} className="px-3 py-3 font-semibold text-navy text-xs uppercase tracking-wider">{h}</th>)}
            </tr></thead>
            <tbody>
              {items.length === 0 ? (
                <tr><td colSpan={8} className="px-4 py-12 text-center text-muted-foreground" data-testid="questions-empty">No questions in this tab yet.</td></tr>
              ) : items.map((q) => (
                <tr key={q.id} className={`border-b border-border last:border-0 hover:bg-slate-50 cursor-pointer ${isOverdue(q) ? "bg-red-50/50" : ""}`}
                    onClick={() => setSelected(q)} data-testid={`question-row-${q.id}`}>
                  <td className="px-3 py-3 max-w-[320px]"><div className="flex items-start gap-1.5">
                    {isCriticalOpen(q) && <AlertTriangle className="h-3.5 w-3.5 text-amber-600 mt-0.5 shrink-0" />}
                    <span className="line-clamp-2">{q.question_text}</span>
                  </div></td>
                  <td className="px-3 py-3 whitespace-nowrap">{q.customer_name}</td>
                  <td className="px-3 py-3 whitespace-nowrap text-xs">
                    {q.control_id ? <Badge className="bg-navy/10 text-navy border-navy/20"><Link2 className="h-3 w-3 mr-1" />{q.control_id}</Badge> : <span className="text-muted-foreground">—</span>}
                    {q.created_from && q.created_from !== "manual" && <div className="text-[10px] text-muted-foreground mt-0.5">from {q.created_from}</div>}
                  </td>
                  <td className="px-3 py-3"><Badge className={PRIORITY_STYLES[q.priority]}>{q.priority}</Badge></td>
                  <td className="px-3 py-3 whitespace-nowrap text-xs">{q.assigned_person || q.assigned_org || "—"}</td>
                  <td className={`px-3 py-3 whitespace-nowrap text-xs ${isOverdue(q) ? "text-red-700 font-semibold" : ""}`}>{q.due_date ? fmtDate(q.due_date) : "—"}</td>
                  <td className="px-3 py-3"><Badge className={QUESTION_STATUS_STYLES[q.status]}>{q.status}</Badge></td>
                  <td className="px-3 py-3 text-right" onClick={(e) => e.stopPropagation()}><div className="flex items-center justify-end gap-1"><ArrowRight className="h-4 w-4 text-muted-foreground cursor-pointer" onClick={() => setSelected(q)} /><RecordActionsMenu type="question" id={q.id} onChanged={load} /></div></td>
                </tr>
              ))}
            </tbody>
          </table></div>
        </div>
      </div>

      <CreateQuestionDialog open={showCreate} onOpenChange={setShowCreate} recons={recons} defaultCategory={tab} onCreated={load} />
      <QuestionDetail q={selected} onClose={() => setSelected(null)} caps={caps} onChanged={(keep) => { load(); setSelected(keep || null); }} />
    </div>
  );
}

function CreateQuestionDialog({ open, onOpenChange, recons, defaultCategory, onCreated }) {
  const [form, setForm] = useState({});
  useEffect(() => { if (open) setForm({ category: defaultCategory, priority: "Medium" }); }, [open, defaultCategory]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const submit = () => {
    if (!form.reconciliation_id) return toast.error("Select a reconciliation.");
    if (!form.question_text?.trim()) return toast.error("Enter the question.");
    api.post("/recon/questions", {
      reconciliation_id: form.reconciliation_id, category: form.category, control_id: form.control_id || "",
      question_text: form.question_text, reason: form.reason || "", assigned_person: form.assigned_person || "",
      priority: form.priority, due_date: form.due_date || "", internal_notes: form.internal_notes || "",
    }).then(() => { toast.success("Question created (Draft)."); onOpenChange(false); onCreated(); })
      .catch((e) => toast.error(e.response?.data?.detail || "Failed to create."));
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="question-create-dialog">
        <DialogHeader><DialogTitle>New Question</DialogTitle>
          <DialogDescription>Drafts must be approved before they can be marked Sent.</DialogDescription></DialogHeader>
        <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
          <div className="space-y-1.5"><Label className="text-xs">Reconciliation *</Label>
            <Select value={form.reconciliation_id || ""} onValueChange={(v) => set("reconciliation_id", v)}>
              <SelectTrigger data-testid="question-create-recon"><SelectValue placeholder="Select reconciliation" /></SelectTrigger>
              <SelectContent>{recons.map((r) => <SelectItem key={r.id} value={r.id}>{r.customer_name} — {r.title || r.framework_name || r.status}</SelectItem>)}</SelectContent>
            </Select></div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label className="text-xs">Directed to</Label>
              <Select value={form.category} onValueChange={(v) => set("category", v)}>
                <SelectTrigger data-testid="question-create-category"><SelectValue /></SelectTrigger>
                <SelectContent>{QUESTION_CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
              </Select></div>
            <div className="space-y-1.5"><Label className="text-xs">Priority</Label>
              <Select value={form.priority} onValueChange={(v) => set("priority", v)}>
                <SelectTrigger data-testid="question-create-priority"><SelectValue /></SelectTrigger>
                <SelectContent>{PRIORITIES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
              </Select></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label className="text-xs">Related control (optional)</Label>
              <Input value={form.control_id || ""} onChange={(e) => set("control_id", e.target.value)} placeholder="e.g. 3.1.1" data-testid="question-create-control" /></div>
            <div className="space-y-1.5"><Label className="text-xs">Due date</Label>
              <Input type="date" value={form.due_date || ""} onChange={(e) => set("due_date", e.target.value)} data-testid="question-create-due" /></div>
          </div>
          <div className="space-y-1.5"><Label className="text-xs">Question *</Label>
            <Textarea rows={3} value={form.question_text || ""} onChange={(e) => set("question_text", e.target.value)} data-testid="question-create-text" /></div>
          <div className="space-y-1.5"><Label className="text-xs">Why we're asking (reason)</Label>
            <Textarea rows={2} value={form.reason || ""} onChange={(e) => set("reason", e.target.value)} data-testid="question-create-reason" /></div>
          <div className="space-y-1.5"><Label className="text-xs">Assigned person</Label>
            <Input value={form.assigned_person || ""} onChange={(e) => set("assigned_person", e.target.value)} data-testid="question-create-assigned" /></div>
          <div className="space-y-1.5"><Label className="text-xs">Internal notes</Label>
            <Textarea rows={2} value={form.internal_notes || ""} onChange={(e) => set("internal_notes", e.target.value)} data-testid="question-create-notes" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button className="bg-gold text-navy hover:bg-gold-dark" onClick={submit} data-testid="question-create-submit">Create Draft</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function QuestionDetail({ q, onClose, caps, onChanged }) {
  const [draft, setDraft] = useState({});
  useEffect(() => { setDraft(q ? { ...q } : {}); }, [q]);
  if (!q) return null;
  const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }));

  const save = (extra = {}) => {
    const payload = {
      question_text: draft.question_text, reason: draft.reason, assigned_person: draft.assigned_person,
      priority: draft.priority, due_date: draft.due_date, answer: draft.answer,
      supporting_evidence: draft.supporting_evidence, internal_notes: draft.internal_notes,
      approved_wording: draft.approved_wording, ...extra,
    };
    return api.put(`/recon/questions/${q.id}`, payload)
      .then((r) => { toast.success("Saved."); onChanged(r.data); })
      .catch((e) => toast.error(e.response?.data?.detail || "Failed to save."));
  };
  const setStatus = (status) => {
    if (status === "Sent" && !["Approved", "Needs Follow-Up", "Sent"].includes(q.status)) {
      return toast.error("Question must be Approved before it can be marked Sent.");
    }
    save({ status });
  };
  const convert = (target) => {
    api.post(`/recon/questions/${q.id}/convert`, { target })
      .then(() => { toast.success(`Converted to ${target}. Source link preserved.`); onChanged(null); })
      .catch((e) => toast.error(e.response?.data?.detail || "Failed to convert."));
  };
  const del = () => {
    api.delete(`/recon/questions/${q.id}`).then(() => { toast.success("Deleted."); onChanged(null); })
      .catch((e) => toast.error(e.response?.data?.detail || "Failed to delete."));
  };
  const statusOptions = QUESTION_STATUSES.filter((s) => !(s === "Approved" && !caps.assign));

  return (
    <Sheet open={!!q} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full sm:max-w-xl overflow-y-auto" data-testid="question-detail-sheet">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2"><MessagesSquare className="h-5 w-5 text-gold" /> {CAT_LABEL[q.category]} question</SheetTitle>
          <SheetDescription>{q.customer_name}{q.control_id ? ` · control ${q.control_id}` : ""}{q.created_from && q.created_from !== "manual" ? ` · from ${q.created_from}` : ""}</SheetDescription>
        </SheetHeader>
        <div className="mt-4 space-y-4">
          <div className="flex flex-wrap items-center gap-2">
            <Badge className={QUESTION_STATUS_STYLES[q.status]}>{q.status}</Badge>
            <Badge className={PRIORITY_STYLES[q.priority]}>{q.priority}</Badge>
            {isOverdue(q) && <Badge className="bg-red-100 text-red-800 border-red-200"><Clock className="h-3 w-3 mr-1" />Overdue</Badge>}
          </div>

          <div className="space-y-1.5"><Label className="text-xs">Question</Label>
            <Textarea rows={3} value={draft.question_text || ""} onChange={(e) => set("question_text", e.target.value)} data-testid="question-detail-text" /></div>
          <div className="space-y-1.5"><Label className="text-xs">Reason</Label>
            <Textarea rows={2} value={draft.reason || ""} onChange={(e) => set("reason", e.target.value)} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label className="text-xs">Priority</Label>
              <Select value={draft.priority} onValueChange={(v) => set("priority", v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{PRIORITIES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
              </Select></div>
            <div className="space-y-1.5"><Label className="text-xs">Due date</Label>
              <Input type="date" value={draft.due_date || ""} onChange={(e) => set("due_date", e.target.value)} /></div>
          </div>
          <div className="space-y-1.5"><Label className="text-xs">Assigned person</Label>
            <Input value={draft.assigned_person || ""} onChange={(e) => set("assigned_person", e.target.value)} /></div>
          <div className="space-y-1.5"><Label className="text-xs">Answer / response</Label>
            <Textarea rows={3} value={draft.answer || ""} onChange={(e) => set("answer", e.target.value)} data-testid="question-detail-answer" />
            {q.answered_by && <p className="text-[11px] text-muted-foreground">Last answered by {q.answered_by} · {fmtDate(q.answer_date)}</p>}</div>
          <div className="space-y-1.5"><Label className="text-xs">Supporting evidence reference</Label>
            <Input value={draft.supporting_evidence || ""} onChange={(e) => set("supporting_evidence", e.target.value)} /></div>
          <div className="space-y-1.5"><Label className="text-xs">Internal notes</Label>
            <Textarea rows={2} value={draft.internal_notes || ""} onChange={(e) => set("internal_notes", e.target.value)} /></div>

          <Button variant="outline" className="w-full" onClick={() => save()} data-testid="question-detail-save">Save changes</Button>

          <div className="border-t border-border pt-4 space-y-2">
            <Label className="text-xs">Status</Label>
            <div className="flex items-center gap-2">
              <Select value={q.status} onValueChange={setStatus}>
                <SelectTrigger data-testid="question-detail-status"><SelectValue /></SelectTrigger>
                <SelectContent>{statusOptions.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {q.status === "Draft" && !caps.assign && <p className="text-[11px] text-amber-700">Only Compliance Admin / Super Admin can approve a Draft.</p>}
          </div>

          <div className="border-t border-border pt-4 space-y-2">
            <Label className="text-xs">Convert (source relationship preserved)</Label>
            <div className="flex flex-wrap gap-2">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" disabled={!caps.assign} data-testid="question-convert-btn"><ArrowRight className="h-3.5 w-3.5 mr-1" /> Convert to…</Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent>
                  <DropdownMenuItem onClick={() => convert("action")} data-testid="question-convert-action">Action Item</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => convert("evidence")} data-testid="question-convert-evidence">Evidence Request</DropdownMenuItem>
                  {q.control_id && <DropdownMenuItem onClick={() => convert("note")} data-testid="question-convert-note">Control Note</DropdownMenuItem>}
                </DropdownMenuContent>
              </DropdownMenu>
              {caps.delete && (
                <Button variant="outline" size="sm" className="text-red-600 border-red-200 hover:bg-red-50" onClick={del} data-testid="question-delete-btn">
                  <Trash2 className="h-3.5 w-3.5 mr-1" /> Delete
                </Button>
              )}
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
